import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LabeledCheckbox, Row, Col } from '@commonsku/styles';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import TopInfo from '../misc/Info';
import { oauth } from '../../../utils';
import { mergeCompanyData } from '../../../redux/companyGeneralData';

type RailField = 'payment_cc' | 'payment_ach';

type Rails = {
  payment_cc: boolean;
  payment_ach: boolean;
};

type ForwardPaymentMethodsProps = {
  data: Record<string, unknown>;
  onChange?: (rails: Rails) => void;
};

export default function ForwardPaymentMethods({ data, onChange }: ForwardPaymentMethodsProps) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const defaultValues = useMemo(() => ({
    payment_cc: true,
    payment_ach: false,
  }), []);

  const [form, setForm] = useState(defaultValues);

  useEffect(() => {
    setForm({
      payment_cc: (data?.forward_payment_cc as boolean) ?? true,
      payment_ach: (data?.forward_payment_ach as boolean) ?? false,
    });
  }, [data?.forward_payment_cc, data?.forward_payment_ach]);

  const toggleField = useCallback((field: RailField) => {
    const next = { ...form, [field]: !form[field] };

    if (!next.payment_cc && !next.payment_ach) {
      // Not allowed to disable both rails
      return;
    }
    setLoading(true);
    onChange?.(next);
    oauth('PUT', 'forward-payments/settings', next).then(({ json }) => {
      dispatch(mergeCompanyData({
        forward_payment_cc: json.payment_cc,
        forward_payment_ach: json.payment_ach,
      }));
    }).catch((err) => {
      setForm(form);
      onChange?.(form);
      toast.error(err?.json?.errors?.[0] ?? "We couldn't save your payment settings. Please try again.");
    }).finally(() => {
      setLoading(false);
    });

    setForm(next);
  }, [dispatch, form, onChange]);

  const getCheckbox = (field: RailField, label: string) => (
    <Col padded sm={12}>
      <LabeledCheckbox label={label} checked={form[field]} onChange={() => toggleField(field)} disabled={loading} />
    </Col>
  );

  return (
    <Row>
      <Col md={12}>
        <TopInfo
          message={(
            <>
              If both Credit Card and ACH are enabled, customers will be able to choose which payment method they want to use.
              The settings on this page will affect the options available to customers for all future payments.
            </>
          )}
          link="#"
          linkLabel="Learn More"
          showLink={false}
        />
      </Col>
      {getCheckbox('payment_ach', 'ACH')}
      {getCheckbox('payment_cc', 'Credit Card')}
    </Row>
  );
}
